<template>
  <Page>
    <ModalCollectionTypeSelect
      v-if="isSelectedModalOpen"
      :title="getCollectionTitle()"
      @saved="onCreateingCollectionComplete"
      @modalClose="toggleTypeModal" />
    <template v-slot:page-header>
      <div class="container">
        <div class="page-header">
          <div class="title-container">
            <div class="title">{{getCollectionTitle()}}</div>
          </div>
          <div class="actions">
            <a class="btn btn-primary text-white" @click="toggleTypeModal">
              <i class="fas fa-plus icon"></i> สร้างใหม่
            </a>
          </div>
        </div>
      </div>
    </template>
    <div class="container">
      <PreloaderSection
        v-if="loadCollectionsState.isLoading" />
      <div v-if="!loadCollectionsState.isLoading">
        <!-- render content -->
        <Section
          v-if="!loadCollectionsState.error">
          <template v-slot:section-filter>
            <div class="search-container">
              <input
                class="form-control form-control-search"
                :value="filter.q"
                :placeholder="`ค้นหาด้วยชื่อ${getCollectionTitle()}`"
                @change="onSearch" />
            </div>
          </template>
          <!-- render content -->
          <template v-slot:section-body>
            <div v-if="!isEmpty()">
              <List>
                <template v-slot:list-header>
                  <div class="list-item-image"></div>
                  <div class="list-item-detail">
                    <div class="list-item list-item-primary list-item-title">ชื่อ</div>
                    <div class="list-item">ประเภท</div>
                    <div class="list-item">สถานะ</div>
                    <div class="list-item list-item-create-at">สร้างเมื่อ</div>
                    <div class="list-item list-item-action"></div>
                  </div>
                </template>
                <template v-slot:list-body>
                  <div class="list-item-container no-underline"
                    v-for="(item, itemIndex) in collections"
                    :key="itemIndex"
                    @click.stop="goToPageDetail(`/collections/${item.id}`, itemIndex, $event)">
                    <div class="list-item-image">
                      <span class="item-status" v-if="hasNewItems(item)">
                        <span class="badge badge-success">ใหม่</span>
                      </span>
                      <ImageView
                        :source="item.coverImage"
                        imageSize="cover160w90h" />
                    </div>
                    <div class="list-item-detail">
                      <div class="list-item list-item-primary list-item-title">
                        <div class="title">
                          {{item.title}}
                          <span class="pin-item" v-if="isPinned(item)">
                          <i class="fas fa-bookmark"></i>
                          </span>
                          <span v-if="isDynamic(item)">
                            <span class="badge badge-light">ไดนามิก</span>
                          </span>
                        </div>
                      </div>
                      <div class="list-item">
                        {{item.type}}
                      </div>
                      <div class="list-item">
                        <div class="badge badge-status" :class="item.status">
                          {{ item.status }}
                        </div>
                      </div>
                      <div class="list-item list-item-create-at">
                        {{formatDateTime(item.createdAt)}}
                      </div>
                      <div class="list-item list-item-action">
                        <div class="action-container">
                          <router-link
                            class="btn btn-light btn-action"
                            :to="`/collections/${item.id}`">
                            <i class="fas fa-pencil-alt"></i>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </List>
              <div
                class="pagination-container"
                v-if="isShowPagination()">
                <Pagination
                  :lastPage="lastPage" />
              </div>
            </div>
            <!-- empty list -->
            <EmptyMessage v-if="isEmpty()">
              <h3>ไม่มีข้อมูล</h3>
              <router-link
                class="btn btn-light btn-lg mt-4"
                :to="`/collections/create`">
                สร้างคอลเลคชั่น
              </router-link>
            </EmptyMessage>
            <EmptyMessage v-if="isEmptySearch()">
              <h3>ไม่พบคอลเลคชั่นที่ค้นหา</h3>
              <button
                type="button"
                class="btn btn-light btn-lg mt-4"
                @click="clearSearch">
                ล้างการค้นหา
              </button>
            </EmptyMessage>
          </template>
        </Section>
        <!-- error from api -->
        <ErrorMessageCollectionContainer
          v-if="loadCollectionsState.error"
          :error="loadCollectionsState.error" />
      </div>
    </div>
  </Page>
</template>

<script>
import _ from 'lodash';
import { loadTaxonomyByKey } from '@/taxonomy/api';
import { loadPrograms } from '@/program/api';
import dateFormetter from '@/utils/dateFormatter';
import Page from '@/layouts/Page.vue';
import Pagination from '@/components/Pagination.vue';
import ImageView from '@/image/ImageView.vue';
import Section from '@/components/Section.vue';
import List from '@/components/List.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import PreloaderSection from '@/components/PreloaderSection.vue';
import ErrorMessageCollectionContainer from '@/collection/ErrorMessageCollectionContainer.vue';
import ModalCollectionTypeSelect from '@/collection/ModalCollectionTypeSelect.vue';

import { loadCollections } from './api';

export default {
  mixins: [dateFormetter],
  components: {
    Page,
    Pagination,
    ImageView,
    Section,
    List,
    EmptyMessage,
    ErrorMessageCollectionContainer,
    PreloaderSection,
    ModalCollectionTypeSelect,
  },
  data() {
    return {
      collections: null,
      lastPage: null,
      programs: null,
      contentCategories: null,
      toggleSidebar: false,
      loadCollectionsState: {
        isLoading: true,
        error: null,
      },
      isSelectedModalOpen: false,
    };
  },
  computed: {
    filter() {
      return this.$route.query;
    },
    hasFilter() {
      return this.programs || this.contentCategories;
    },
  },
  watch: {
    filter(newValue) {
      this.load(newValue);
    },
  },
  created() {
    this.load(this.filter);
    this.loadContentCategories();
    this.loadPrograms();
  },
  methods: {
    async load(filter) {
      try {
        const response = await loadCollections(filter);
        this.collections = response.data.data;
        this.lastPage = response.data.lastPage;
        this.loadCollectionsState.isLoading = false;
        this.loadCollectionsState.error = null;
      } catch (error) {
        this.loadCollectionsState.error = error.response;
        this.loadCollectionsState.isLoading = false;
      }
    },
    async loadContentCategories() {
      const response = await loadTaxonomyByKey('content-category');
      this.contentCategories = response.data.data;
    },
    async loadPrograms(filter) {
      const response = await loadPrograms(filter);
      this.programs = response.data.data;
    },
    getCreateLink() {
      if (this.filter && this.filter.type) {
        return `/collections/create?type=${this.filter.type}`;
      }
      return '/collections/create';
    },
    getCollectionTitle() {
      if (this.filter && this.filter.type) {
        const collectionType = this.filter.type.toLowerCase();
        switch (collectionType) {
          case 'video':
            return 'เพลย์ลิสต์';
          case 'playlist':
            return 'ชุดเพลย์ลิสต์';
          case 'episode':
            return 'ชุดตอน';
          case 'program':
            return 'ชุดรายการ';
          default:
            return 'คอลเล็คชั่น';
        }
      }
      return 'คอลเล็คชั่น';
    },
    isEmptySearch() {
      return _.isEmpty(this.collections) && !_.every(this.filter, _.isEmpty);
    },
    isEmpty() {
      return _.isEmpty(this.collections) && _.every(this.filter, _.isEmpty);
    },
    onSearch(event) {
      let filter;
      if (event.target.value) {
        filter = { ...this.filter, q: event.target.value };
      } else {
        filter = { ...this.filter };
        delete filter.q;
      }
      this.$router.push({ query: { ...filter } });
    },
    clearSearch() {
      const filter = null;
      this.$router.push({ query: { ...filter } });
    },
    isShowPagination() {
      return this.lastPage > 1;
    },
    hasNewItems(item) {
      return item.hasNewItems;
    },
    isPinned(item) {
      return item.isPinned;
    },
    isDynamic(item) {
      return item.isDynamic;
    },
    toggleTypeModal() {
      this.isSelectedModalOpen = !this.isSelectedModalOpen;
    },
    onCreateingCollectionComplete(id) {
      this.isSelectedModalOpen = false;
      this.$router.push(`/collections/${id}`);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/form.scss';

.toggle {
  transition: all 0.3s ease-out;
  margin-right: 250px;
}

.pin-item {
  margin-left: $spacer * 0.25;
}


.list-item-image {
  position: relative;
  .item-status {
    position: absolute;
    top: 4px;
    left: 4px;
    z-index: 1;
    font-size: 80%;
    .badge {
      padding: 4px 6px;
    }
  }
}

@media (max-width: 575.98px) {

  .toggle {
    margin-right: 0px;
  }

  .list-item-container {

    .list-item-detail {
      .list-item {
        font-size: $font-size-base * 0.875;
        width: 70%;
        display: none;

        &.list-item-primary {
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
          padding: $spacer * 0.25;
        }

        &.list-item-secondary {
          display: flex;
          order: -1;
          padding: $spacer * 0.25;
          font-size: $font-size-base * 0.875;
        }

        &.list-item-status {
          display: flex;
        }
      }
    }
  }
}

@media (min-width: 576px) and (max-width: 1023.98px) {
  .toggle {
    margin-right: 0px;
  }

  .list-item-container {
    .list-item-detail {
      .list-item {
        &.list-item-create-at {
          display: none;
        }
        &.list-item-date {
          display: none;
        }
        &.list-item-action {
          .action-container {
            .btn-external {
              display: none;
            }
          }
        }
      }
    }
  }
}
</style>
